import React, { forwardRef, useState, type ReactNode } from "react";
import {
  View,
  TextInput,
  Pressable,
  StyleSheet,
  type TextInputProps,
  type ViewStyle,
  Platform,
} from "react-native";

import { Box } from "../Box";
import { ErrorText } from "../ErrorText";
import { ChevronDown } from "../icons";
import { Label } from "../Label";
import { Text } from "../Text";
import { getFontSizeStyle } from "../Text/TextLevel";
import { useTheme } from "../ThemeProvider";
import type { StrictPaddingStyle } from "../util/types";
import { useFocusRing } from "../util/useFocusRing";

export interface PhoneInputProps {
  /**
   * Label text to display above the input
   */
  label?: string;
  /**
   * Whether the input is required
   */
  isRequired?: boolean;
  /**
   * Accessibility text to denote the field is required to screenreaders.
   * @default "required"
   */
  requiredText?: string;
  /**
   * Additional explainer text shown below the label
   */
  explainerText?: string;
  /**
   * Error message to display below the input
   */
  errorText?: string;
  /**
   * Accessibility text to denote the field is invalid to screenreaders.
   * @default "invalid"
   */
  invalidText?: string;
  /**
   * Icon to display before the label
   */
  icon?: ReactNode;
  /**
   * The current phone number value
   */
  value?: string;
  /**
   * Callback when the phone number changes
   */
  onChangeText?: (text: string) => void;
  /**
   * The current area code
   */
  areaCode: number;
  /**
   * Country flag to display next to the area code.
   *
   * **Important:** Do not use emoji flag characters (e.g. 🇺🇸) here.
   * Windows has no native emoji flag support, so they render as letter pairs ("US")
   * or empty boxes in Chrome/Edge on Windows.
   * Use something like an `<Image>` component with a real flag image (PNG/SVG/WebP) instead.
   *
   * @example
   * ```tsx
   * countryFlag={
   *   <Image
   *     source={{ uri: 'https://flagcdn.com/w20/us.png' }}
   *     style={{ width: 20, height: 15 }}
   *     accessible={false}
   *     accessibilityElementsHidden={true}
   *     importantForAccessibility="no-hide-descendants"
   *   />
   * }
   * ```
   */
  countryFlag?: ReactNode;
  /**
   * Name of the currently selected country, used to derive the accessible label
   * for the area code button (e.g. "United States" → "Flag United States, +1").
   * When omitted the label falls back to "+{areaCode}".
   */
  countryName?: string;
  /**
   * Callback when the area code button is pressed
   */
  onAreaCodePress?: () => void;
  /**
   * Placeholder text for the phone number input
   * @default "XXX-XXX-XXXX"
   */
  placeholder?: string;
  /**
   * Whether the input is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Additional styles for the container
   */
  style?: ViewStyle;
  /**
   * Callback when the text input loses focus
   */
  onBlur?: () => void;
  /**
   * Additional props for the TextInput
   */
  inputProps?: Omit<TextInputProps, "value" | "onChangeText">;

  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;
  /**
   * Used to locate this view in end-to-end tests and for accessibility IDs.
   */
  testID?: string;
}

type TextInputA11yProps = Partial<
  Pick<TextInputProps, "accessibilityLabel" | "accessibilityHint">
> & {
  /**
   * We keep this list intentionally narrow to avoid type conflicts with RN's own `aria-*` typings.
   */
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  "aria-required"?: boolean;
  "aria-invalid"?: boolean;
};

/**
 * Builds accessibility props for the TextInput based on platform
 */
function buildA11yProps(
  label: string | undefined,
  isRequired: boolean | undefined,
  requiredText: string,
  errorText: string | undefined,
  invalidText: string,
  explainerText: string | undefined,
  testID: string,
): TextInputA11yProps {
  const is_web = Platform.OS === "web";

  if (!is_web) {
    // Native mobile: use accessibilityLabel and accessibilityHint directly
    let accessibilityLabel = label ?? "";
    if (isRequired) {
      accessibilityLabel = `${accessibilityLabel}, ${requiredText}`;
    }
    if (errorText) {
      accessibilityLabel = `${accessibilityLabel}, ${invalidText}`;
    }

    let accessibilityHint = "";
    if (errorText) {
      accessibilityHint = errorText;
    }
    if (explainerText) {
      accessibilityHint = accessibilityHint
        ? `${accessibilityHint}. ${explainerText}`
        : explainerText;
    }

    return {
      accessibilityLabel: accessibilityLabel || undefined,
      accessibilityHint: accessibilityHint || undefined,
    };
  }

  // Web: use aria attributes with IDs
  const labelId = `${testID}-label-text`;
  const explainerTextId = `${testID}-explainer-text`;
  const errorTextId = `${testID}-error-text`;

  return {
    "aria-labelledby": label ? labelId : undefined,
    "aria-describedby": errorText
      ? explainerText
        ? `${errorTextId} ${explainerTextId}`
        : errorTextId
      : explainerText
        ? explainerTextId
        : undefined,
    "aria-required": isRequired || undefined,
    "aria-invalid": !!errorText || undefined,
  };
}

export const PhoneInput = forwardRef<TextInput, PhoneInputProps>(
  (
    {
      label,
      isRequired,
      requiredText = "required",
      explainerText,
      errorText,
      invalidText = "invalid",
      icon,
      value = "",
      onChangeText,
      areaCode,
      countryName,
      countryFlag,
      onAreaCodePress,
      onBlur,
      placeholder = "XXX-XXX-XXXX",
      disabled = false,
      inputProps,
      sx,
      testID = "phone-input",
    }: PhoneInputProps,
    ref,
  ) => {
    const theme = useTheme();
    const [isFocused, setIsFocused] = useState(false);
    const [isAreaCodeHovered, setIsAreaCodeHovered] = useState(false);
    const { isFocusVisible, focusProps } = useFocusRing();
    const is_android = Platform.OS === "android";
    const is_web = Platform.OS === "web";

    const resolvedAreaCodeLabel = countryName ? `${countryName}, +${areaCode}` : `+${areaCode}`;

    // IDs for Label component (needed for Android accessibilityLabelledBy workaround)
    const labelId = `${testID}-label-text`;
    const explainerTextId = `${testID}-explainer-text`;
    const errorTextId = `${testID}-error-text`;

    const a11yProps = buildA11yProps(
      label,
      isRequired,
      requiredText,
      errorText,
      invalidText,
      explainerText,
      testID,
    );

    const styles = StyleSheet.create({
      container: {
        flexDirection: "row",
        alignItems: "center",
        minHeight: 48,
        borderWidth: 1,
        borderColor: theme.colors.border,
        borderRadius: theme.borderRadius.md,
        backgroundColor: theme.colors.background,
      },
      areaCodeButton: {
        backgroundColor: isAreaCodeHovered ? theme.colors.backgroundActionHover : undefined,
        paddingLeft: theme.spacing.s3,
        paddingRight: theme.spacing.s3,
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: theme.spacing.s2,
        borderTopLeftRadius: theme.borderRadius.md,
        borderBottomLeftRadius: theme.borderRadius.md,
      },
      areaCodeText: {
        marginRight: theme.spacing.s1,
      },
      flagContainer: {
        marginRight: theme.spacing.s2,
      },
      input: {
        minHeight: 48,
        flex: 1,
        borderColor: theme.colors.foreground,
        borderLeftWidth: 1,
        justifyContent: "center",
      },
      inputFocused: {
        borderColor: theme.colors.borderPrimary,
        borderTopRightRadius: theme.borderRadius.md,
        borderBottomRightRadius: theme.borderRadius.md,
        borderLeftWidth: 3,
        borderWidth: 3,
      },
      disabled: {
        opacity: 0.4,
      },
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      textInput: {
        marginLeft: theme.spacing.s3,
        color: theme.colors.foreground,
        fontSize: getFontSizeStyle(3, theme).fontSize,
        outlineStyle: Platform.OS === "web" ? "none" : undefined,
      } as unknown as ViewStyle,
      textInputFocused: {
        marginLeft: theme.spacing.s3 - 2, // Account for change in border width.
      },
      outerContainer: {
        gap: theme.spacing.s1,
      },
      focusBorderColor: {
        borderColor: theme.colors.borderPrimary,
      },
      errorBorderColor: {
        borderColor: theme.colors.borderStatusCriticalStrong,
      },
    });

    return (
      <View style={[styles.outerContainer, disabled && styles.disabled, sx]}>
        {label && (
          <Label
            text={label}
            labelID={labelId}
            isRequired={isRequired}
            requiredText={requiredText}
            explainerText={explainerText}
            explainerTextID={explainerTextId}
            isInvalid={!!errorText}
            invalidText={invalidText}
            icon={icon}
            testID={`${testID}-label`}
            renderStatesToScreenReader={is_android}
          />
        )}
        <View
          testID={testID}
          style={[
            styles.container,
            isFocused && { borderColor: theme.colors.border },
            errorText && { borderColor: theme.colors.borderStatusCriticalStrong },
          ]}
        >
          <Pressable
            onPress={onAreaCodePress}
            disabled={disabled}
            style={[
              styles.areaCodeButton,
              errorText && styles.errorBorderColor,
              isFocusVisible && {
                outlineStyle: "solid",
                outlineWidth: 2,
                outlineColor: theme.colors.utilityFocus,
                outlineOffset: 2,
              },
            ]}
            onFocus={focusProps.onFocus}
            onBlur={focusProps.onBlur}
            testID={`${testID}-area-code`}
            accessibilityRole="button"
            accessibilityLabel={resolvedAreaCodeLabel}
            aria-label={is_web ? resolvedAreaCodeLabel : undefined}
            onHoverIn={() => setIsAreaCodeHovered(true)}
            onHoverOut={() => setIsAreaCodeHovered(false)}
          >
            {countryFlag && <View style={styles.flagContainer}>{countryFlag}</View>}
            <Text level={3} sx={styles.areaCodeText}>
              +{areaCode}
            </Text>
            <Box pointerEvents="none" focusable={false}>
              <ChevronDown
                aria-hidden={true}
                variant="filled"
                color={disabled ? theme.colors.foreground : theme.colors.foreground}
              />
            </Box>
          </Pressable>
          <Box
            sx={[
              styles.input,
              isFocused && styles.inputFocused,
              isFocused && !errorText && styles.focusBorderColor,
              errorText && styles.errorBorderColor,
            ]}
          >
            <TextInput
              ref={ref}
              value={value}
              style={[styles.textInput, isFocused && styles.textInputFocused]}
              onChangeText={onChangeText}
              placeholder={placeholder}
              placeholderTextColor={theme.colors.foregroundWeak}
              editable={!disabled}
              aria-disabled={disabled}
              accessibilityState={{ disabled: disabled }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => {
                setIsFocused(false);
                onBlur?.();
              }}
              keyboardType="phone-pad"
              testID={`${testID}-input`}
              {...a11yProps}
              {...inputProps}
            />
          </Box>
        </View>
        {errorText && <ErrorText nativeID={errorTextId}>{errorText}</ErrorText>}
      </View>
    );
  },
);

PhoneInput.displayName = "PhoneInput";
